/**
 * Session 60.26 · HoursLocationLink
 *
 * Reusable pill-shaped hyperlink for a Métier Hours location. Used
 * anywhere the app displays a clickable link to a specific café —
 * Commons check-in posts, Profile › Places I've Worked, and the
 * check-in success surface.
 *
 * Design (per Cameron's spec):
 *   [ 📍 CoffeeFi Roasters ↗ ]
 *   - Métier gold location-pin (lucide MapPin) on the left
 *   - Cream / warm off-white rounded pill background
 *   - Gold text
 *   - Small arrow (lucide ArrowUpRight) after the name
 *   - Fully-rounded pill corners, generous horizontal padding
 *   - Entire pill is a single tap target (≥44px min-height on mobile)
 *   - Long names truncate gracefully rather than overflowing
 *
 * The link deep-links to `/hours?cafe=<cafe_id>` — always internal,
 * never straight to the café's external website. That is a distinct
 * "Visit Café" CTA that lives on the Hours directory itself.
 *
 * Props
 *   cafeId    — canonical hours_cafes.cafe_id (required for the deep-link)
 *   name      — display name of the café / location
 *   className — optional extra class for consumer-side layout
 *   testid    — optional data-testid override
 */
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";

export default function HoursLocationLink({ cafeId, name, className, testid }) {
  if (!cafeId || !name) return null;

  return (
    <Link
      to={`/hours?cafe=${cafeId}`}
      className={className}
      data-testid={testid || `hours-location-link-${cafeId}`}
      aria-label={`Open ${name} in Métier Hours`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 40,
        maxWidth: "100%",
        padding: "8px 16px",
        borderRadius: 999,
        background: "#FAF3E4",
        border: "1px solid rgba(184,145,58,0.28)",
        color: "#8B6A22",
        textDecoration: "none",
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: 1.2,
        boxShadow: "0 1px 2px rgba(139,106,34,0.06)",
        transition: "background 140ms ease, border-color 140ms ease, transform 140ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#F5E9CE";
        e.currentTarget.style.borderColor = "rgba(184,145,58,0.5)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#FAF3E4";
        e.currentTarget.style.borderColor = "rgba(184,145,58,0.28)";
      }}
    >
      <MapPin
        size={16}
        strokeWidth={2}
        aria-hidden="true"
        style={{ flexShrink: 0, color: "#B8913A" }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "min(60vw, 320px)",
        }}
      >
        {name}
      </span>
      <ArrowUpRight
        size={14}
        strokeWidth={2.25}
        aria-hidden="true"
        style={{ flexShrink: 0, opacity: 0.75, marginLeft: 2 }}
      />
    </Link>
  );
}
